import { useState, useEffect } from 'react';
import { supabase } from '../supabase';

// Cache keyed by user ID — staff creation never poisons admin's cache
const _cache = {};

// Which user the cache was last resolved for. Lets a cached answer be applied
// synchronously on mount instead of only after a round trip.
let _lastUserId = null;

const useAdminPermissions = () => {
  // Seeding from cache means a repeat visit starts resolved rather than
  // entering a loading state it will leave microseconds later. That loading
  // state is a full-screen gate in PermissionGate, so skipping it removes a
  // visible flash on every admin navigation. The fetch below still runs and
  // corrects the seed if the signed-in user turns out to be someone else.
  const seed = _lastUserId ? _cache[_lastUserId] : null;

  const [permissions, setPermissions] = useState(seed ? seed.permissions : null);
  const [role, setRole] = useState(seed ? seed.role : null);
  const [loading, setLoading] = useState(!seed);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    fetchPermissions();
  }, []);

  const fetchPermissions = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setLoading(false); return; }

    _lastUserId = user.id;

    // Return cached result for THIS specific user
    if (_cache[user.id]) {
      setPermissions(_cache[user.id].permissions);
      setRole(_cache[user.id].role);
      setLoading(false);
      return;
    }

    // Everyone, including the super admin, is read from admin_users. There is
    // no hardcoded identity here any more: authority is a row you can grant,
    // revoke and audit rather than a constant that needs a redeploy.
    //
    // maybeSingle() rather than single(): a non-admin signing in is a normal
    // outcome, not an error to throw on.
    const { data, error } = await supabase
      .from('admin_users')
      .select('role, is_frozen, admin_permissions(*)')
      .eq('id', user.id)
      .maybeSingle();

    if (error) {
      // Distinguish "not an admin" from "could not tell". Denying on a failed
      // read is right, but it must be visible rather than silent, or a policy
      // problem looks identical to a permissions problem.
      console.error('[useAdminPermissions] could not read admin_users:', error.message);
      setLoadError(error.message);
      setLoading(false);
      return;
    }

    // A frozen admin is treated as no admin at all.
    if (data && !data.is_frozen) {
      // PostgREST decides embed cardinality from constraints. With only a
      // foreign key on admin_id this arrives as an array; the moment a UNIQUE
      // constraint is added it becomes a bare object instead. Handling both
      // means that migration cannot silently blank every staff permission.
      const raw = data.admin_permissions;
      const perms = (Array.isArray(raw) ? raw[0] : raw) || {};

      _cache[user.id] = { permissions: perms, role: data.role };
      setPermissions(perms);
      setRole(data.role);
    }
    setLoading(false);
  };

  // Role is the source of truth for super admin. The old version read
  // permissions.is_super_admin, which only ever existed on the object the
  // hardcoded short-circuit built by hand — so once that constant went, a real
  // super_admin row would have been denied everything.
  const can = (permission) => {
    if (role === 'super_admin') return true;
    if (!permissions) return false;
    return !!permissions[permission];
  };

  const isSuperAdmin = role === 'super_admin';

  return { permissions, role, loading, loadError, can, isSuperAdmin };
};

// Call on logout to clear all cached permissions. Resetting _lastUserId is not
// optional: leaving it set would let the next person to sign in be seeded with
// the previous user's permissions for the moment before the fetch resolves.
export const clearPermissionsCache = () => {
  Object.keys(_cache).forEach(k => delete _cache[k]);
  _lastUserId = null;
};

export default useAdminPermissions;